import { eq, sql } from "drizzle-orm";
import { useLoaderData } from "react-router";
import { AuthUser } from "~/auth.server";
import { ItemsBlock } from "~/components/blocs/ItemsBlock";
import { LastCommentsBlock } from "~/components/blocs/LastCommentsBlock";
import { LatestActivity } from "~/components/blocs/LatestActivity";
import { NpcsBlock } from "~/components/blocs/NpcsBlock";
import { PopularPages } from "~/components/blocs/PopularPages";
import { QuestBlock } from "~/components/blocs/QuestBlock";
import {
  commentsWithObjects,
  LastCommentWithObject,
} from "~/components/site/Comments";
import { GverMenu } from "~/components/site/GverMenu";
import { Informer } from "~/components/site/Informer";
import { Heading2 } from "~/components/ui/Heading";
import { PageTitle } from "~/components/ui/PageTitle";
import { pg } from "~/pg.server";
import { gameName } from "~/util/common";
import { GameVer, lstring, Realm, useRealm, useT } from "~/util/Lang";
import type { GetLoaderData, MetaFunc, OurLoaderArgs } from "~/util/Loaders";
import {
  latestActivityQuery,
  LatestActivityQueryWithObject,
  pagesWithObjects,
} from "~/util/page";
import { logPageView } from "~/util/stats";
import { PopularPage, topPagesQuery } from "~/util/top";

export const loader = async (args: OurLoaderArgs) => {
  // Log
  logPageView(args);
  //
  const commentsPerPage = 15;

  const comments = await pg.db
    .select({
      id: pg.comments.id,
      content: pg.comments.content,
      objType: pg.comments.objType,
      objUname: pg.comments.objUname,
      createdAt: pg.comments.createdAt,
      page: sql<number>`FLOOR((ROW_NUMBER() OVER (PARTITION BY obj_type, obj_uname ORDER BY created_at DESC) - 1) / ${commentsPerPage}) + 1`.as(
        "page"
      ),
      lang: pg.comments.lang,
      game: pg.comments.game,
    })
    .from(pg.comments)
    .where(eq(pg.comments.isDeleted, false))
    .orderBy(sql`created_at DESC`)
    .limit(15);

  const activities = await latestActivityQuery();

  return {
    authUser: args.req.sess?.user,
    comments: commentsWithObjects({ comments, args }),
    feed: pagesWithObjects({ activities, args }),
    popularPages: await topPagesQuery({ args }),
  };
};

export const meta: MetaFunc<{}> = (cx) => {
  return {
    title: getTitle(cx),
    description: getDesc(cx),
  };
};

type LoaderData = GetLoaderData<typeof loader>;

export default function Index() {
  const { game } = useRealm();
  const data = useLoaderData() as LoaderData;
  const t = useT();

  return <>{informer(game, data)}</>;
}

function informer(
  game: GameVer,
  data: {
    authUser?: AuthUser;
    comments: LastCommentWithObject[];
    feed: LatestActivityQueryWithObject[];
    popularPages: PopularPage[];
  }
) {
  const t = useT();
  switch (game) {
    case "c1":
    case "c2":
    case "c3":
      return (
        <>
          <div style={{ marginTop: 10 }}>
            <div className="columns" style={{ flexDirection: "row-reverse" }}>
              <div className="column">
                <div className="content bg" style={{ marginBottom: 20 }}>
                  <PopularPages
                    pages={data.popularPages}
                    authUser={data.authUser}
                  />
                </div>
                <div className="content bg">
                  <LastCommentsBlock comments={data.comments} />
                </div>
              </div>
              <div className="column is-three-quarters bg">
                <div className="content bg">
                  <div style={{ margin: "-10px 0 20px 0" }}>
                    <GverMenu />
                  </div>
                  <PageTitle>
                    {t({
                      en: `The unofficial knowledge base for the game ${gameName(
                        game
                      )}`,
                      ru: `Русская неофициальная версия базы знаний по игре ${gameName(
                        game
                      )}`,
                    })}
                  </PageTitle>
                  <ItemsBlock />
                  <NpcsBlock />
                  <Heading2>Latest Activity</Heading2>
                  <LatestActivity data={data.feed} />
                </div>
              </div>
            </div>
          </div>
        </>
      );
    case "c4":
    case "c5":
    case "il":
    case "gf":
      return (
        <>
          <div style={{ marginTop: 10 }}>
            <div className="columns" style={{ flexDirection: "row-reverse" }}>
              <div className="column">
                <div className="content bg" style={{ marginBottom: 20 }}>
                  <PopularPages
                    pages={data.popularPages}
                    authUser={data.authUser}
                  />
                </div>
                <div className="content bg">
                  <LastCommentsBlock comments={data.comments} />
                </div>
              </div>
              <div className="column is-three-quarters bg">
                <div className="content bg">
                  <div style={{ margin: "-10px 0 20px 0" }}>
                    <GverMenu />
                  </div>
                  <PageTitle>
                    {t({
                      en: `The unofficial knowledge base for the game ${gameName(
                        game
                      )}`,
                      ru: `Русская неофициальная версия базы знаний по игре ${gameName(
                        game
                      )}`,
                    })}
                  </PageTitle>
                  <QuestBlock />
                  <ItemsBlock />
                  <NpcsBlock />
                  <Heading2>Latest Activity</Heading2>
                  <LatestActivity data={data.feed} />
                </div>
              </div>
            </div>
          </div>
        </>
      );
    default:
      return <Informer />;
  }
}

function getDesc(cx: { realm: Realm; t: (str: lstring) => string }) {
  return cx.t({
    en: `An unofficial knowledge base for the ${gameName(
      cx.realm.game
    )} game, containing guides and information about classes, items, quests, non-player characters and much more.`,
    ru: `Неофициальная Русская версия базы знаний по игре ${gameName(
      cx.realm.game
    )}, в нашей базе знаний вы найдете полный список квестов с наградами, список всех скиллов и классов, всех НПЦ с точным положением на карте, а так же точный дроп и спойл предметов согласно версии игры ${gameName(
      cx.realm.game
    )}.`,
  });
}

function getTitle(cx: { realm: Realm; t: (str: lstring) => string }) {
  return cx.t({
    en: `L2Hub.info - ${gameName(cx.realm.game)} game Knowledge Base`,
    ru: `L2Hub.info - База знаний по игре ${gameName(cx.realm.game)}`,
  });
}
